import React from "react";
import {Link, useTranslation} from "gatsby-plugin-react-i18next";
import {StaticImage} from "gatsby-plugin-image";
import {ArrowRight} from "@phosphor-icons/react";
import SectionHeading from "./section-heading";

const Stat = ({value, label}) => (
    <div className="flex flex-col gap-1">
        <span className="cl-num text-2xl sm:text-3xl font-bold leading-none whitespace-nowrap text-[var(--cl-accent)]">{value}</span>
        <span className="text-[13px] leading-snug text-[var(--cl-muted)]">{label}</span>
    </div>
);

const ShortCard = ({to, logo, value, label, text, name}) => (
    <Link to={to} className="group bg-[var(--cl-surface)] p-6 flex flex-col gap-4 hover:bg-[var(--cl-surface-2)] transition-colors">
        <div className="h-10 flex items-center">{logo}</div>
        {value ? (
            <p className="flex items-baseline gap-2">
                <span className="cl-num text-2xl font-bold text-[var(--cl-accent)]">{value}</span>
                <span className="text-[13px] leading-snug text-[var(--cl-muted)]">{label}</span>
            </p>
        ) : null}
        <p className="text-[15px] leading-relaxed text-[var(--cl-ink-2)]">{text}</p>
        <span className="mt-auto pt-2 inline-flex items-center gap-1.5 text-[15px] font-semibold text-[var(--cl-ink)]">
            {name}
            <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-1"/>
        </span>
    </Link>
);

const CaseStudies = () => {
    const {t} = useTranslation();

    return (
        <section className="flex flex-col w-full max-w-screen-2xl mx-auto px-5 gap-10">
            <SectionHeading
                eyebrow={t("Customers")}
                title={t("Five companies.|What changed after they switched the agent on.")}
                lead={t("Fewer e-mails, shorter queues, answers at night. The numbers below come from the customers themselves.")}
            />

            <div className="bg-[var(--cl-surface)] border border-[var(--cl-rule)] grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-px bg-[var(--cl-rule)]">
                <div className="bg-[var(--cl-surface)] p-8 sm:p-10 flex flex-col gap-6">
                    <StaticImage
                        src="../../images/customerlogos/bmhlogo.webp"
                        width={150}
                        alt="Black Mountain Honey"
                        className="w-[120px]"
                        placeholder="none"
                        formats={["webp"]}
                    />
                    <div className="grid grid-cols-3 gap-6">
                        <Stat value="80%" label={t("of enquiries answered by the agent")}/>
                        <Stat value="-70%" label={t("routine e-mail volume")}/>
                        <Stat value="30 000" label={t("visitors a month served around the clock")}/>
                    </div>
                    <p className="text-lg leading-relaxed text-[var(--cl-ink-2)]">
                        "{t("Buzz handles most of our clients' questions instantly and cuts routine e-mail volume by over 70%. He never sleeps, so our visitors get answers around the clock and my team focuses on complex orders.")}"
                    </p>
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <StaticImage
                                src="../../images/laurenceedwards.webp"
                                alt="Laurence Edwards"
                                className="w-11 h-11 rounded-full overflow-hidden shrink-0"
                                imgClassName="w-full h-full object-cover"
                                placeholder="none"
                                width={88}
                                height={88}
                                formats={["webp"]}
                            />
                            <p className="text-[14px] text-[var(--cl-muted)]">
                                <span className="font-bold text-[var(--cl-ink)]">Laurence Edwards</span><br/>
                                {t("Owner, Black Mountain Honey")}
                            </p>
                        </div>
                        <Link to="/usecase/bmh/" className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-[var(--cl-ink)] underline-offset-4 hover:underline">
                            {t("Read the case study")} <ArrowRight size={14} weight="bold"/>
                        </Link>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[var(--cl-rule)]">
                    <ShortCard
                        to="/usecase/olimplabs/"
                        logo={<StaticImage src="../../images/ecommerce/olimp_logo1.webp" alt="Olimp Labs" height={40} placeholder="none" formats={["webp"]}/>}
                        value="-70%"
                        label={t("enquiries forwarded to the team")}
                        text={t("E-mails down by around 40%, and the shop sells more.")}
                        name="Olimp Labs"
                    />
                    <ShortCard
                        to="/usecase/umili/"
                        logo={<StaticImage src="../../images/hotel/logo-umili-retina.webp" alt="Umili Resorts" height={30} placeholder="none" formats={["webp"]}/>}
                        value="-60%"
                        label={t("cases escalated to the front desk")}
                        text={t("70% of routine guest enquiries handled automatically.")}
                        name="Umili Resorts"
                    />
                    <ShortCard
                        to="/usecase/inmusic/"
                        logo={<StaticImage src="../../images/customerlogos/inmusic.png" alt="inMusic" height={28} placeholder="none" formats={["webp"]}/>}
                        text={t("Support for a global instrument brand, tuned live without touching the running bot.")}
                        name="inMusic"
                    />
                    <ShortCard
                        to="/usecase/wieliczka/"
                        logo={<StaticImage src="../../images/customers/kopalnia_soli_wieliczka_circle.png" alt="Wieliczka Salt Mine" height={44} placeholder="none" formats={["webp"]}/>}
                        text={t("Visitor questions answered in several languages for a UNESCO site.")}
                        name={t("Wieliczka Salt Mine")}
                    />
                </div>
            </div>
        </section>
    );
};

export default CaseStudies;
